/**
 * The gallery paints its dark editorial background by forcing white/grey text
 * onto every text-bearing element beneath it, so arbitrary CMS module markup
 * reads on black. Product-tagging chrome — the in-image tag card and all four
 * tagging modal screens — is a light glass panel living inside that same
 * subtree, so every rule that can reach it stops at `.product-tagging-chrome`
 * instead of repainting it afterwards. Repainting flattened the panel's own
 * hierarchy: result titles and their supplier lines are separate greys
 * (charcoal 300 / charcoal 100), as are the `text-gray-500` helper captions and
 * the tag card's supplier link. Excluding the chrome lets each of those
 * authored colours — and weights — stand as its component wrote them.
 *
 * The chrome root then takes the body colour so anything inside it that carries
 * no colour of its own inherits dark-on-light rather than the white it would
 * otherwise pick up from the gallery.
 *
 * Shared by the public gallery section and the Business Manager page builder so
 * the editing surface cannot drift from what the visitor sees. Always scope it
 * to a container — the legacy and light pages must stay untouched.
 */
export const PROJECT_MODULE_DARK_TEXT = [
    '[&_.editor-quote]:border-white/20!',
    '[&_.ui-caption:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:font-sans!',
    '[&_.ui-caption:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:font-normal!',
    '[&_.ui-caption:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-[#cbcbcb]!',
    // The muted link colour is for links inside the copy. A platform button
    // that happens to be an anchor — the tagged product's View product — takes
    // its own ink from the button, so it is exempt the way the tagging chrome is.
    '[&_a:not(.product-tagging-chrome):not(.product-tagging-chrome_*):not(.detail-cta-pill)]:font-sans!',
    '[&_a:not(.product-tagging-chrome):not(.product-tagging-chrome_*):not(.detail-cta-pill)]:text-[#a3a3a3]!',
    '[&_blockquote:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_div:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_h1:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_h2:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_h3:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_h4:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_h5:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_h6:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_li:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_p:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:font-sans!',
    '[&_p:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_.product-tagging-chrome]:text-[#1f1f21]!',
].join(' ');

/**
 * Business Manager page builder controls on the revamp's dark ground. Pair it
 * with PROJECT_MODULE_DARK_TEXT on the same container.
 *
 * The editor's controls are Aria components with light colours baked into
 * their Fela styles, so this repaints them through the stable class hooks the
 * builder puts on each control, in the public page's button language:
 * - hover controls and `page-builder-primary-action` take the white primary
 *   button (#323232 ink);
 * - `page-builder-secondary-action` takes the outlined glass button;
 * - dashed drop targets use a white/30 rule instead of charcoal.
 *
 * Modals render in portals outside this container, and product-tagging chrome
 * is excluded, so both keep their light panels exactly as the visitor sees
 * them. Rules that recolour text carry the same chrome exclusion as
 * PROJECT_MODULE_DARK_TEXT, which also lifts them above its forced white.
 */
export const PROJECT_BUILDER_DARK_CHROME = [
    // Surfaces that paint their own white over the dark ground.
    '[&_.page-builder-section]:bg-transparent!',
    '[&_.page-builder-bulk-upload]:bg-transparent!',
    '[&_.page-builder-bulk-upload-landing]:bg-[#181818]!',
    '[&_.upload-loading]:bg-[#181818]/75!',
    // Hover controls (module toolbar, image toolbar, module name, add-section
    // dots) take the white primary pill: they float over both the dark ground
    // and photographs, so they need the strongest contrast the page has.
    '[&_.page-builder-section-actions]:bg-white!',
    '[&_.page-builder-section-actions]:shadow-[0_8px_24px_rgba(0,0,0,0.5)]!',
    '[&_.page-builder-section_.ui-button:not(.product-tagging-chrome_*):not(.page-builder-primary-action):not(.page-builder-secondary-action)]:bg-white!',
    '[&_.page-builder-section_.ui-button:not(.product-tagging-chrome_*):not(.page-builder-primary-action):not(.page-builder-secondary-action)]:text-[#323232]!',
    '[&_.page-builder-section_.ui-button:not(.product-tagging-chrome_*):not(.page-builder-primary-action):not(.page-builder-secondary-action):hover]:bg-[#e5e5e5]!',
    // The banner sits outside any module section; its image toolbar and video
    // delete action share this hook.
    '[&_.hover-buttons-image_.ui-button]:bg-white!',
    '[&_.hover-buttons-image_.ui-button]:text-[#323232]!',
    '[&_.hover-buttons-image_.ui-button:hover]:bg-[#e5e5e5]!',
    '[&_.page-builder-section-title]:bg-white!',
    '[&_.page-builder-section-title:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-[#323232]!',
    '[&_.page-builder-new-section_.ui-avatar]:bg-white!',
    '[&_.page-builder-new-section_svg]:fill-[#323232]!',
    // Primary and secondary actions: the public page's two button styles.
    '[&_.page-builder-primary-action]:bg-white!',
    '[&_.page-builder-primary-action]:text-[#323232]!',
    '[&_.page-builder-primary-action:not(.product-tagging-chrome):not(.product-tagging-chrome_*)_*]:text-[#323232]!',
    '[&_.page-builder-primary-action_svg]:fill-[#323232]!',
    '[&_.page-builder-secondary-action]:bg-black/40!',
    '[&_.page-builder-secondary-action]:border-white/60!',
    '[&_.page-builder-secondary-action]:text-white!',
    // Drop targets and their helper copy.
    '[&_.page-builder-add-section]:bg-transparent!',
    '[&_.page-builder-add-section]:border-white/30!',
    '[&_.page-builder-add-section:hover]:bg-white/5!',
    '[&_.page-builder-add-section_*]:text-[#f2f2f2]!',
    '[&_.page-builder-add-section_svg]:fill-[#f2f2f2]!',
    '[&_.page-builder-bulk-upload_form]:border-white/30!',
    '[&_.page-builder-bulk-upload_.ui-label]:text-white!',
    '[&_.page-builder-bulk-upload_.ui-caption]:text-white/70!',
    '[&_.upload-instructions_svg]:fill-[#f2f2f2]!',
    '[&_.upload-instructions:not(.product-tagging-chrome):not(.product-tagging-chrome_*)_*]:text-white/70!',
    '[&_.rich-text-editor-placeholder:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white/40!',
    // Sync overlay: a dark scrim rather than a white flash over the module.
    '[&_.page-builder-syncing-overlay]:bg-[#181818]/85!',
    '[&_.page-builder-syncing-spinner]:bg-[#323232]!',
    '[&_.page-builder-syncing-spinner_svg]:stroke-[#f2f2f2]!',
].join(' ');

/**
 * Figma spaces the gallery modules 3.12rem (50px) apart on desktop, overriding
 * the renderer's default gap-12. The module list is a single-column grid with an
 * implicit `auto` track, so one module wider than the column (a full-bleed block
 * in some galleries) sizes the track to its max-content and drags every module —
 * including the centred full-page images — right. Pin the track to the container
 * so a wide module can overflow on its own without shifting the rest.
 */
export const PROJECT_MODULE_GRID = 'grid-cols-[minmax(0,1fr)] md:gap-[3.12rem]';
